/**
 * Admin sidebar navigation
 */

import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Upload,
  FileSpreadsheet,
  MapPin,
  Building2,
  Landmark,
  Users,
  Home,
  ArrowLeft,
  Layers,
  Globe,
  ListTodo,
  ShieldAlert,
  Map,
  BarChart3,
  BarChart2,
  Hexagon,
  SlidersHorizontal,
  Compass,
  Table,
  Scale,
  FileText,
  FileCog,
  MessageSquareText,
} from 'lucide-react'
import { cn } from '../../lib/utils'

interface NavItemProps {
  to: string
  icon: React.ReactNode
  label: string
  end?: boolean
}

function NavItem({ to, icon, label, end = false }: NavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 rounded-gut8 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-text-inverse'
            : 'text-gut8-anthracite hover:bg-primary-subtle hover:text-primary-dark'
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  )
}

export function AdminSidebar() {
  return (
    <aside className="w-64 bg-gut8-bg-muted border-r border-border-gut8-light min-h-screen relative">
      <div className="p-4 border-b border-border-gut8-light">
        <h1 className="text-xl font-bold text-gut8-anthracite">Admin</h1>
        <p className="text-xs text-text-muted mt-1">Lagewerkzeug</p>
      </div>

      <nav className="px-2 py-4 space-y-1 pb-20">
        {/* Dashboard */}
        <NavItem
          to="/admin"
          end
          icon={<LayoutDashboard className="w-5 h-5" />}
          label="Dashboard"
        />

        {/* Datenquellen */}
        <div className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider mt-4">
          Datenquellen
        </div>

        <NavItem
          to="/admin/import"
          end
          icon={<Upload className="w-5 h-5" />}
          label="Gemeindeverzeichnis"
        />

        <NavItem
          to="/admin/genesis"
          icon={<BarChart2 className="w-5 h-5" />}
          label="GENESIS"
        />

        <NavItem
          to="/admin/inkar"
          icon={<BarChart3 className="w-5 h-5" />}
          label="INKAR"
        />

        <NavItem
          to="/admin/deutschlandatlas"
          icon={<Compass className="w-5 h-5" />}
          label="Deutschlandatlas"
        />

        <NavItem
          to="/admin/indicators"
          icon={<SlidersHorizontal className="w-5 h-5" />}
          label="Indikatoren"
        />

        <NavItem
          to="/admin/import/csv"
          icon={<FileSpreadsheet className="w-5 h-5" />}
          label="CSV Importe"
        />

        {/* Gutachterliche Config */}
        <div className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider mt-4">
          Gutachterliche Config
        </div>

        <NavItem
          to="/admin/gutachten"
          end
          icon={<FileCog className="w-5 h-5" />}
          label="Übersicht"
        />

        <NavItem
          to="/admin/report-layout"
          icon={<Table className="w-5 h-5" />}
          label="Lagedaten-Layout"
        />

        <NavItem
          to="/admin/rating-config"
          icon={<Scale className="w-5 h-5" />}
          label="Wertungen"
        />

        <NavItem
          to="/admin/prompts"
          icon={<FileText className="w-5 h-5" />}
          label="KI-Prompt"
        />

        <NavItem
          to="/admin/description-config"
          icon={<MessageSquareText className="w-5 h-5" />}
          label="Beschreibungs-Texte"
        />

        {/* Jobs & Quarantine */}
        <div className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider mt-4">
          Verwaltung
        </div>

        <NavItem
          to="/admin/jobs"
          icon={<ListTodo className="w-5 h-5" />}
          label="Jobs"
        />

        <NavItem
          to="/admin/quarantine"
          icon={<ShieldAlert className="w-5 h-5" />}
          label="Quarantäne"
        />

        {/* Divider */}
        <div className="my-4 border-t border-border-gut8-light" />

        {/* Entity Tables */}
        <div className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
          Entitäten
        </div>

        <NavItem
          to="/admin/federal"
          icon={<Globe className="w-5 h-5" />}
          label="Bund"
        />

        <NavItem
          to="/admin/states"
          icon={<MapPin className="w-5 h-5" />}
          label="Bundesländer"
        />

        <NavItem
          to="/admin/government-districts"
          icon={<Building2 className="w-5 h-5" />}
          label="Regierungsbezirke"
        />

        <NavItem
          to="/admin/counties"
          icon={<Landmark className="w-5 h-5" />}
          label="Kreise"
        />

        <NavItem
          to="/admin/associations"
          icon={<Users className="w-5 h-5" />}
          label="Gemeindeverbände"
        />

        <NavItem
          to="/admin/municipalities"
          icon={<Home className="w-5 h-5" />}
          label="Gemeinden"
        />

        {/* Divider */}
        <div className="my-4 border-t border-border-gut8-light" />

        {/* Klassifikationen */}
        <div className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
          Klassifikationen
        </div>

        <NavItem
          to="/admin/regionalisierung"
          icon={<Map className="w-5 h-5" />}
          label="Regionalisierung"
        />

        <NavItem
          to="/admin/typisierung"
          icon={<Layers className="w-5 h-5" />}
          label="Typisierung"
        />

        {/* Divider */}
        <div className="my-4 border-t border-border-gut8-light" />

        {/* Statistische Entitäten */}
        <div className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
          Statistische Entitäten
        </div>

        <NavItem
          to="/admin/statistische-entitaeten"
          icon={<Hexagon className="w-5 h-5" />}
          label="BBSR-Regionen"
        />
      </nav>

      {/* Back to App */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border-gut8-light bg-gut8-bg-muted">
        <NavLink
          to="/"
          className="flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zur App
        </NavLink>
      </div>
    </aside>
  )
}
